/**
 * Turning a stored subscription into sentences a person can act on (auth#435).
 *
 * ── WHY THIS IS A MODULE AND NOT JSX ────────────────────────────────
 *
 * Every branch here is a claim about somebody's money: whether they still have
 * access, whether a payment failed, what ended and when. The repo already keeps
 * decisions of that weight out of components for exactly this reason — see
 * `sessionState` in App.tsx, extracted after a conflation between "signed out"
 * and "the session lookup failed" cost an hour of debugging at the wrong layer.
 * A pure function can be tested against every status the service can send; a
 * component can only be tested against the ones somebody thought to render.
 *
 * ── THE SERVICE HAS ALREADY DECIDED. NOTHING IS RE-DERIVED HERE ─────
 *
 * `status` is the answer, and `currentPeriodEnd` is not consulted to second-
 * guess it. auth's `resolveRow` says why at length: an `ACTIVE` row whose
 * period end has passed means a renewal event is merely late, and RevenueCat
 * retries for 155 minutes, so a screen that expired it on the clock would tell
 * a paid-up customer they had been cut off. This file re-applying the clock
 * would reintroduce that in the one place the customer is looking.
 */

/**
 * One subscription as the account API sends it.
 *
 * `currentPeriodEnd` is a STRING. The endpoint stringifies the Date on purpose
 * (see auth's `plugins/account.ts`), because a client written against a `Date`
 * that is really a string fails as "Invalid Date" on whichever screen formats
 * it, a long way from the call.
 */
export interface PortalSubscription {
  appSlug: string;
  /** The application's display name. The slug is an identifier, not copy. */
  appName: string;
  tier: string;
  /** `ACTIVE`, `PAST_DUE`, `EXPIRED`, or `NONE`. */
  status: string;
  /** ISO 8601, or null on a lifetime entitlement AND when nothing is held. */
  currentPeriodEnd: string | null;
  /** The tier that lapsed. Null unless `status` is `EXPIRED`. */
  endedTier: string | null;
  /**
   * Which storefront this was bought through, verbatim from RevenueCat, or null.
   *
   * NULL IS A NORMAL AND PERMANENT ANSWER, not a gap waiting on a backfill:
   * RevenueCat sends the field only "sometimes", reconciliation cannot supply it
   * (`active_entitlements` carries no store), a promotional grant has no
   * storefront at all, and every row written before auth had the column has
   * none. So the "we cannot say which shop" path below stays reachable for good.
   */
  store: string | null;
}

export interface SubscriptionCopy {
  /**
   * The plan to show as this row's current plan, or null when the row names
   * none that the person still holds.
   *
   * Null for an expired row, and that is not tidiness: auth writes `FREE` over
   * the tier when a subscription lapses and moves the real one to `endedTier`.
   * Rendering `tier` unconditionally would therefore label a lapsed Premium row
   * "Free", which reads as a downgrade somebody chose rather than one that
   * happened to them. The detail sentence names what ended instead.
   */
  tierLabel: string | null;
  /** The status, in words, for the row. */
  headline: string;
  tone: 'ok' | 'bad' | 'muted';
  /** The sentence under the headline, or null when there is nothing true to add. */
  detail: string | null;
}

const ACTIVE = 'ACTIVE';
const PAST_DUE = 'PAST_DUE';
const EXPIRED = 'EXPIRED';
const NONE = 'NONE';

/**
 * `PREMIUM` to `Premium`, `PREMIUM_PLUS` to `Premium plus`.
 *
 * The tier is the PRODUCT's vocabulary and this service does not interpret it
 * (auth's schema says so), so this is presentation and nothing more: no map of
 * known tiers, because a second paid app naming its tiers differently must not
 * need a change here to be readable. Shouting the stored constant at somebody
 * is the only outcome actually ruled out.
 */
export function tierLabel(tier: string): string {
  const words = tier.replace(/_/g, ' ').trim();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * The default date format: long, and in the reader's own locale.
 *
 * Injectable because the copy is what gets tested, and `toLocaleDateString`
 * answers differently depending on where the test runs. A test that pinned an
 * en-GB string would pass here and fail in CI for a reason having nothing to do
 * with subscriptions.
 */
export const formatDay = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { dateStyle: 'long' });

/**
 * What to say about one subscription.
 *
 * ── "Paid up to", never "renews" ────────────────────────────────────
 *
 * We cannot tell the two apart and must not guess. auth writes `ACTIVE` both
 * for a subscription that will renew and for one somebody has already
 * cancelled in the store but is still inside the paid period of. "Renews on the
 * 9th" is therefore a coin flip, and it is wrong in the direction that costs
 * the customer money: somebody who cancelled and is told it renews will not
 * go and check.
 *
 * ── `PAST_DUE` IS THE ROW THIS SCREEN EXISTS FOR ────────────────────
 *
 * It is the one state where the customer has to do something and where nothing
 * else tells them. They keep their access through the store's retry window, so
 * the app looks completely normal; the issue calls this out, and adr/0016 names
 * keyodo collapsing `PAST_DUE` into `FREE` as the bug that justified moving the
 * lifecycle into auth. Saying "active" here would be the same mistake with the
 * opposite sign.
 */
export function describeSubscription(
  sub: PortalSubscription,
  format: (iso: string) => string = formatDay,
): SubscriptionCopy {
  const day = sub.currentPeriodEnd ? format(sub.currentPeriodEnd) : null;

  if (sub.status === ACTIVE) {
    return {
      tierLabel: tierLabel(sub.tier),
      headline: 'Active',
      tone: 'ok',
      detail: day
        ? `Paid up to ${day}.`
        : // No end date is a lifetime entitlement, which auth is explicit must
          // never read as expired. Said positively so it cannot.
          'This does not expire.',
    };
  }

  if (sub.status === PAST_DUE) {
    return {
      tierLabel: tierLabel(sub.tier),
      headline: 'Payment failed',
      tone: 'bad',
      detail: day
        ? `You still have access until ${day} while the store retries the payment. Update your payment details to keep it.`
        : 'The store is retrying the payment. Update your payment details to keep your access.',
    };
  }

  if (sub.status === EXPIRED) {
    const ended = sub.endedTier ? tierLabel(sub.endedTier) : null;
    return {
      tierLabel: null,
      headline: 'Ended',
      tone: 'muted',
      detail: ended
        ? day
          ? `Your ${ended} plan ended on ${day}.`
          : `Your ${ended} plan has ended.`
        : /*
             `endedTier` is null when the lapsed row no longer names a paid
             tier, and auth is explicit that "your Free plan ended" is not a
             sentence anybody should be shown. So the plan goes unnamed rather
             than named wrongly.
          */
          day
          ? `This ended on ${day}.`
          : 'This has ended.',
    };
  }

  if (sub.status === NONE) {
    // The list is built from rows that exist, so this should not arrive. Handled
    // anyway, and handled as "nothing held" rather than dropped, because a row
    // the screen silently omits is indistinguishable from one it failed to load.
    return { tierLabel: null, headline: 'Not subscribed', tone: 'muted', detail: null };
  }

  /**
   * AN UNRECOGNISED STATUS IS NOT ASSUMED TO BE ANY OF THE ABOVE.
   *
   * auth owns this lifecycle and can add to it; this client would then be shown
   * a value it has never heard of. Falling through to "Active" would grant
   * reassurance nothing supports, and falling through to "Ended" would tell
   * somebody their subscription had stopped when it had not. Both are worse
   * than admitting the client is behind, which is also the only version of this
   * that is visible enough to get fixed.
   */
  return {
    tierLabel: tierLabel(sub.tier),
    headline: 'Status unavailable',
    tone: 'muted',
    detail: 'We could not read the status of this subscription. Check the store you bought it from.',
  };
}

const APPLE = 'https://apps.apple.com/account/subscriptions';
const GOOGLE = 'https://play.google.com/store/account/subscriptions';

/**
 * The stores we can send somebody straight to.
 *
 * ── ONLY TWO ENTRIES, AND THE SHORT LIST IS THE POINT ───────────────
 *
 * Apple and Google publish a stable, account-level subscriptions page that
 * works for any signed-in customer. The others do not: Stripe and RevenueCat
 * web billing need a portal session minted per customer, and Paddle and Roku
 * have no equivalent page at all. Inventing a plausible URL for those would
 * produce the single worst outcome this screen can have — a confident link that
 * 404s somebody who is trying to stop being charged.
 *
 * So a store we cannot link is NAMED instead, which still moves somebody
 * forward, and the generic pair stays available as the fallback below.
 */
const STORE_LINKS: Record<string, string> = {
  APP_STORE: APPLE,
  // A Mac purchase is managed in the same Apple account page. Listed explicitly
  // rather than matched on a prefix, because `store` is a third party's enum
  // and a prefix rule would quietly capture whatever they add next.
  MAC_APP_STORE: APPLE,
  PLAY_STORE: GOOGLE,
};

/**
 * What to call each store in a sentence, for the ones we cannot link.
 *
 * RevenueCat's documented values as of 2026-09-12. Absence from this map is not
 * an error: a value that arrives unmapped is described generically rather than
 * shouted as a constant, because the list is theirs to extend and it has grown
 * twice already.
 */
const STORE_NAMES: Record<string, string> = {
  APP_STORE: 'the App Store',
  MAC_APP_STORE: 'the Mac App Store',
  PLAY_STORE: 'Google Play',
  AMAZON: 'the Amazon Appstore',
  STRIPE: 'Stripe',
  RC_BILLING: 'Ridgewill web billing',
  PADDLE: 'Paddle',
  ROKU: 'Roku',
};

/** A promotional grant. Not a purchase, and there is no shop behind it. */
const PROMOTIONAL = 'PROMOTIONAL';

export interface StoreRoute {
  /** Where to send them, or null when we have no page we can stand behind. */
  href: string | null;
  /** The sentence on the row. Never null: silence here is the unhelpful answer. */
  sentence: string;
}

/**
 * Where THIS subscription is changed.
 *
 * ── WHY THE ROW IS ASKED AND THE BROWSER IS NOT ─────────────────────
 *
 * auth now records the storefront the purchase came through, so this is a
 * lookup rather than a guess. Guessing from the user agent was the alternative
 * and it is wrong for exactly the person most likely to be on this screen:
 * somebody who bought on their iPhone and is now on a laptop sorting out their
 * billing.
 */
export function storeRoute(store: string | null): StoreRoute {
  if (store === PROMOTIONAL) {
    // Nothing to manage, and saying so is kinder than a link to a store that
    // has never heard of this subscription.
    return { href: null, sentence: 'Given to you by Ridgewill, so there is nothing to cancel.' };
  }

  const href = store ? (STORE_LINKS[store] ?? null) : null;
  if (href) {
    return { href, sentence: `Manage this in ${STORE_NAMES[store as string] ?? 'the store'}.` };
  }

  if (store) {
    const named = STORE_NAMES[store];
    return {
      // Named but not linked. Still useful: it tells somebody which of their
      // accounts to go and look in, which is most of the job.
      href: null,
      sentence: named
        ? `Billed through ${named}, so any change is made there.`
        : 'Billed through a store outside Ridgewill, so any change is made there.',
    };
  }

  /**
   * NO STORE RECORDED, which is permanent for some rows rather than a gap.
   *
   * Deliberately vague, because being specific would mean guessing. The generic
   * pair below is what such a reader follows.
   */
  return {
    href: null,
    sentence: 'We do not have a record of where this was bought. Check the stores below.',
  };
}

/**
 * The generic fallback pair, for a reader whose row could not be linked.
 *
 * Still needed even now that rows carry a store: see `PortalSubscription.store`
 * for the four ways a live row legitimately has none, and note that somebody can
 * be paying and have NO ROW yet, because a purchase made before signing in sits
 * on an anonymous store customer until the app reconciles it. That arrival has
 * nothing else on this screen to follow.
 */
export const STORES = [
  { label: 'Apple subscriptions', href: APPLE },
  { label: 'Google Play subscriptions', href: GOOGLE },
] as const;

/**
 * Does the generic pair still need showing?
 *
 * ── KEYED ON "WE DO NOT KNOW", NOT ON "WE COULD NOT LINK" ───────────
 *
 * Those look interchangeable and are not, which is the whole subtlety here. A
 * Stripe row and a promotional grant both fail to produce a link, but Apple and
 * Google are of no use to either: one is billed somewhere else entirely and the
 * other was never bought. Offering the pair for those rows would answer a
 * question the reader did not ask, and for the promotional row it would
 * contradict the sentence directly above it.
 *
 * The pair earns its place in exactly two situations: a row whose store is
 * unrecorded, where those two are the likely candidates, and NO ROWS AT ALL,
 * because somebody can be paying and have no row yet.
 */
export function needsStoreFallback(subscriptions: PortalSubscription[]): boolean {
  if (subscriptions.length === 0) return true;
  return subscriptions.some((sub) => sub.store === null);
}
